import type { Exercise } from '@/types';

/**
 * Catalogue d'exercices.
 *
 * `requires` = équipements strictement nécessaires.
 * `substitutes` = liste ordonnée par préférence ; le builder de séance choisit le premier
 * dont l'équipement est présent. Format : `{ when: 'no_<equipmentId>', use: '<exId>' }`.
 * `zones` = zones musculaires touchées (utilisé pour pondération zones prioritaires).
 */
export const EXERCISES: Record<string, Exercise> = {
  pompes: {
    id: 'pompes', n: 'Pompes', m: 'Pec', e: 'Aucun', mw: 'push up',
    requires: [], zones: ['chest', 'arms'],
    levelVariants: { advanced: 'pompes_diamant' },
  },
  pompes_genoux: {
    id: 'pompes_genoux', n: 'Pompes sur genoux', m: 'Pec', e: 'Aucun', mw: 'knee push up',
    requires: [], zones: ['chest', 'arms'],
    // Pompes genoux = pour vrai débutant. Intermediate fait de vraies pompes, Advanced du diamant.
    levelVariants: { intermediate: 'pompes', advanced: 'pompes_diamant' },
  },
  pompes_inclinees: {
    id: 'pompes_inclinees', n: 'Pompes inclinées (banc)', m: 'Pec', e: 'Banc', mw: 'incline push up',
    requires: ['bench'], zones: ['chest', 'arms'],
    substitutes: [{ when: 'no_bench', use: 'pompes' }],
    // Pour Advanced, on inverse : pompes déclinées (pieds surélevés) = bien plus dur
    levelVariants: { advanced: 'pompes_declinees' },
  },
  pompes_diamant: {
    id: 'pompes_diamant', n: 'Pompes diamant', m: 'Pec/Triceps', e: 'Aucun', mw: 'diamond push up',
    requires: [], zones: ['chest', 'arms'],
  },
  pompes_declinees: {
    id: 'pompes_declinees', n: 'Pompes déclinées (pieds surélevés)', m: 'Pec haut', e: 'Banc', mw: 'decline push up',
    requires: ['bench'], zones: ['chest', 'arms', 'shoulders'],
    substitutes: [{ when: 'no_bench', use: 'pompes_diamant' }],
  },
  pompes_archer: {
    id: 'pompes_archer', n: 'Pompes archer', m: 'Pec/Triceps', e: 'Aucun', mw: 'archer push up',
    requires: [], zones: ['chest', 'arms'],
  },
  developpe_h: {
    id: 'developpe_h', n: 'Développé couché haltères', m: 'Pec', e: 'Haltères + banc', mw: 'dumbbell bench press',
    requires: ['dumbbells', 'bench'], zones: ['chest', 'arms'],
    substitutes: [
      { when: 'no_bench', use: 'pompes_inclinees' },
      // Fallback robuste si ni banc ni surface inclinée disponible
      { when: 'no_bench', use: 'pompes' },
      { when: 'no_dumbbells', use: 'pompes' },
    ],
  },
  ecarte_h: {
    id: 'ecarte_h', n: 'Écarté haltères', m: 'Pec', e: 'Haltères + banc', mw: 'dumbbell fly',
    requires: ['dumbbells', 'bench'], zones: ['chest'],
    substitutes: [
      { when: 'no_bench', use: 'pompes' },
      { when: 'no_dumbbells', use: 'pompes' },
    ],
  },
  pullover_h: {
    id: 'pullover_h', n: 'Pullover haltère', m: 'Pec/Dos', e: 'Haltère + banc', mw: 'dumbbell pullover',
    requires: ['dumbbells', 'bench'], zones: ['chest', 'back'],
    substitutes: [
      { when: 'no_bench', use: 'rowing_h_sol' },
      { when: 'no_bench', use: 'pompes' },
      { when: 'no_dumbbells', use: 'pompes' },
    ],
  },
  traction: {
    id: 'traction', n: 'Tractions pronation', m: 'Dos', e: 'Barre', mw: 'pull up',
    requires: ['pullup_bar'], zones: ['back', 'arms'],
    substitutes: [{ when: 'no_pullup_bar', use: 'rowing_h' }],
    levelVariants: { beginner: 'traction_assistee', advanced: 'traction_lestee' },
  },
  traction_sup: {
    id: 'traction_sup', n: 'Tractions supination (chin-up)', m: 'Dos/Biceps', e: 'Barre', mw: 'chin up',
    requires: ['pullup_bar'], zones: ['back', 'arms'],
    substitutes: [{ when: 'no_pullup_bar', use: 'rowing_h' }],
    levelVariants: { beginner: 'traction_assistee', advanced: 'traction_lestee' },
  },
  traction_assistee: {
    id: 'traction_assistee', n: 'Tractions assistées (élastique ou jambes au sol)', m: 'Dos/Biceps', e: 'Barre + élastique', mw: 'assisted pull up',
    requires: ['pullup_bar'], zones: ['back', 'arms'],
    substitutes: [{ when: 'no_pullup_bar', use: 'rowing_h' }],
  },
  traction_lestee: {
    id: 'traction_lestee', n: 'Tractions lestées', m: 'Dos/Biceps', e: 'Barre + lest', mw: 'weighted pull up',
    requires: ['pullup_bar'], zones: ['back', 'arms'],
    substitutes: [{ when: 'no_pullup_bar', use: 'rowing_h' }],
  },
  rowing_h: {
    id: 'rowing_h', n: 'Rowing haltère 1 bras', m: 'Dos', e: 'Haltère + banc', mw: 'one arm dumbbell row',
    requires: ['dumbbells', 'bench'], zones: ['back', 'arms'],
    substitutes: [
      { when: 'no_bench', use: 'rowing_h_sol' },
      { when: 'no_dumbbells', use: 'pompes' },
    ],
  },
  rowing_h_sol: {
    id: 'rowing_h_sol', n: 'Rowing haltère penché (sans banc)', m: 'Dos', e: 'Haltères', mw: 'bent over dumbbell row',
    requires: ['dumbbells'], zones: ['back', 'arms'],
    substitutes: [{ when: 'no_dumbbells', use: 'pompes' }],
  },
  shrug_h: {
    id: 'shrug_h', n: 'Shrug (trapèzes)', m: 'Trapèzes', e: 'Haltères', mw: 'dumbbell shrug',
    requires: ['dumbbells'], zones: ['back', 'shoulders'],
  },
  developpe_mil: {
    id: 'developpe_mil', n: 'Développé militaire haltères', m: 'Épaules', e: 'Haltères', mw: 'dumbbell shoulder press',
    requires: ['dumbbells'], zones: ['shoulders', 'arms'],
    substitutes: [{ when: 'no_dumbbells', use: 'pompes' }],
  },
  elev_lat: {
    id: 'elev_lat', n: 'Élévations latérales', m: 'Épaules', e: 'Haltères', mw: 'lateral raise',
    requires: ['dumbbells'], zones: ['shoulders'],
  },
  elev_front: {
    id: 'elev_front', n: 'Élévations frontales', m: 'Épaules', e: 'Haltères', mw: 'front raise',
    requires: ['dumbbells'], zones: ['shoulders'],
  },
  oiseau: {
    id: 'oiseau', n: 'Oiseau (deltoïde postérieur)', m: 'Épaules', e: 'Haltères', mw: 'reverse fly',
    requires: ['dumbbells'], zones: ['shoulders', 'back'],
  },
  curl_h: {
    id: 'curl_h', n: 'Curl biceps haltères', m: 'Biceps', e: 'Haltères', mw: 'dumbbell curl',
    requires: ['dumbbells'], zones: ['arms'],
  },
  curl_marteau: {
    id: 'curl_marteau', n: 'Curl marteau', m: 'Biceps', e: 'Haltères', mw: 'hammer curl',
    requires: ['dumbbells'], zones: ['arms'],
  },
  dips_banc: {
    id: 'dips_banc', n: 'Dips entre 2 bancs', m: 'Triceps', e: 'Banc', mw: 'bench dip',
    requires: ['bench'], zones: ['arms', 'chest'],
    substitutes: [{ when: 'no_bench', use: 'pompes' }],
    levelVariants: { advanced: 'dips_lestes' },
  },
  dips_lestes: {
    id: 'dips_lestes', n: 'Dips lestés', m: 'Triceps', e: 'Banc + lest', mw: 'weighted bench dip',
    requires: ['bench'], zones: ['arms', 'chest'],
    substitutes: [{ when: 'no_bench', use: 'pompes_diamant' }],
  },
  ext_tri: {
    id: 'ext_tri', n: 'Extension triceps haltère', m: 'Triceps', e: 'Haltère', mw: 'overhead tricep extension',
    requires: ['dumbbells'], zones: ['arms'],
  },
  kickback: {
    id: 'kickback', n: 'Kickback triceps', m: 'Triceps', e: 'Haltère', mw: 'tricep kickback',
    requires: ['dumbbells'], zones: ['arms'],
  },
  squat_goblet: {
    id: 'squat_goblet', n: 'Goblet squat', m: 'Quadriceps', e: 'Haltère', mw: 'goblet squat',
    requires: ['dumbbells'], zones: ['quads', 'glutes'],
    substitutes: [{ when: 'no_dumbbells', use: 'squat_pdc' }],
    levelVariants: { advanced: 'squat_pistolet' },
  },
  squat_pdc: {
    id: 'squat_pdc', n: 'Squat poids du corps', m: 'Quadriceps', e: 'Aucun', mw: 'bodyweight squat',
    requires: [], zones: ['quads', 'glutes'],
    levelVariants: { intermediate: 'squat_goblet', advanced: 'squat_pistolet' },
  },
  squat_pistolet: {
    id: 'squat_pistolet', n: 'Squat pistolet (1 jambe)', m: 'Quadriceps', e: 'Aucun', mw: 'pistol squat',
    requires: [], zones: ['quads', 'glutes'],
  },
  fentes: {
    id: 'fentes', n: 'Fentes avant', m: 'Jambes', e: 'Haltères', mw: 'dumbbell lunge',
    requires: ['dumbbells'], zones: ['quads', 'glutes'],
    substitutes: [{ when: 'no_dumbbells', use: 'fentes_pdc' }],
  },
  fentes_pdc: {
    id: 'fentes_pdc', n: 'Fentes avant (sans charge)', m: 'Jambes', e: 'Aucun', mw: 'bodyweight lunge',
    requires: [], zones: ['quads', 'glutes'],
  },
  fentes_bulgares: {
    id: 'fentes_bulgares', n: 'Fentes bulgares', m: 'Quadriceps', e: 'Haltères + banc', mw: 'bulgarian split squat',
    requires: ['bench'], zones: ['quads', 'glutes'],
    substitutes: [{ when: 'no_bench', use: 'fentes' }],
  },
  sdt_roumain: {
    id: 'sdt_roumain', n: 'Soulevé de terre roumain', m: 'Ischios', e: 'Haltères', mw: 'romanian deadlift dumbbell',
    requires: ['dumbbells'], zones: ['glutes', 'back'],
    substitutes: [{ when: 'no_dumbbells', use: 'hip_thrust_pdc' }],
  },
  hip_thrust: {
    id: 'hip_thrust', n: 'Hip thrust', m: 'Fessiers', e: 'Haltère + banc', mw: 'hip thrust',
    requires: ['bench'], zones: ['glutes'],
    substitutes: [{ when: 'no_bench', use: 'hip_thrust_pdc' }],
  },
  hip_thrust_pdc: {
    id: 'hip_thrust_pdc', n: 'Glute bridge (sans banc)', m: 'Fessiers', e: 'Aucun', mw: 'glute bridge',
    requires: [], zones: ['glutes'],
  },
  mollets: {
    id: 'mollets', n: 'Mollets debout', m: 'Mollets', e: 'Aucun', mw: 'standing calf raise',
    requires: [], zones: ['calves'],
  },
  planche: {
    id: 'planche', n: 'Planche abdominale', m: 'Abdos', e: 'Aucun', mw: 'plank', t: true,
    requires: [], zones: ['abs'],
    levelVariants: { advanced: 'planche_dynamique' },
  },
  planche_dynamique: {
    id: 'planche_dynamique', n: 'Planche dynamique (shoulder taps)', m: 'Abdos', e: 'Aucun', mw: 'plank shoulder tap', t: true,
    requires: [], zones: ['abs', 'shoulders'],
  },
  planche_lat: {
    id: 'planche_lat', n: 'Planche latérale', m: 'Obliques', e: 'Aucun', mw: 'side plank', t: true,
    requires: [], zones: ['abs'],
  },
  crunch: {
    id: 'crunch', n: 'Crunch', m: 'Abdos', e: 'Aucun', mw: 'crunch',
    requires: [], zones: ['abs'],
  },
  rt: {
    id: 'rt', n: 'Russian twist', m: 'Obliques', e: 'Haltère', mw: 'russian twist',
    requires: [], zones: ['abs'],
  },
  mc: {
    id: 'mc', n: 'Mountain climbers', m: 'Abdos', e: 'Aucun', mw: 'mountain climber', t: true,
    requires: [], zones: ['abs'],
  },
  hollow: {
    id: 'hollow', n: 'Hollow body hold', m: 'Abdos', e: 'Aucun', mw: 'hollow body hold', t: true,
    requires: [], zones: ['abs'],
  },
  releve_jambes: {
    id: 'releve_jambes', n: 'Relevés jambes suspendu', m: 'Abdos bas', e: 'Barre', mw: 'hanging leg raise',
    requires: ['pullup_bar'], zones: ['abs'],
    substitutes: [{ when: 'no_pullup_bar', use: 'hollow' }],
  },
  v_ups: {
    id: 'v_ups', n: 'V-ups', m: 'Abdos', e: 'Aucun', mw: 'v up',
    requires: [], zones: ['abs'],
  },
  shadow: {
    id: 'shadow', n: 'Shadow boxing', m: 'Boxe', e: 'Aucun', mw: 'shadow boxing', t: true,
    requires: [], zones: ['shoulders', 'abs'],
  },
  sac_jab: {
    id: 'sac_jab', n: 'Sac : jab–cross', m: 'Boxe', e: 'Sac + gants', mw: 'heavy bag jab cross', t: true,
    requires: ['punching_bag', 'gloves'], zones: ['shoulders', 'arms', 'abs'],
    substitutes: [
      { when: 'no_punching_bag', use: 'shadow' },
      { when: 'no_gloves', use: 'shadow' },
    ],
  },
  sac_combos: {
    id: 'sac_combos', n: 'Sac : combos complets', m: 'Boxe', e: 'Sac + gants', mw: 'heavy bag combinations', t: true,
    requires: ['punching_bag', 'gloves'], zones: ['shoulders', 'arms', 'abs'],
    substitutes: [
      { when: 'no_punching_bag', use: 'shadow' },
      { when: 'no_gloves', use: 'shadow' },
    ],
  },
  sac_puissance: {
    id: 'sac_puissance', n: 'Sac : puissance', m: 'Boxe', e: 'Sac + gants', mw: 'heavy bag power', t: true,
    requires: ['punching_bag', 'gloves'], zones: ['shoulders', 'arms', 'chest'],
    substitutes: [
      { when: 'no_punching_bag', use: 'shadow' },
      { when: 'no_gloves', use: 'shadow' },
    ],
  },
  corde: {
    id: 'corde', n: 'Corde à sauter', m: 'Cardio', e: 'Corde', mw: 'jump rope', t: true,
    requires: ['jump_rope'], zones: ['calves'],
    substitutes: [{ when: 'no_jump_rope', use: 'mc' }],
  },
  velo: {
    id: 'velo', n: 'Vélo', m: 'Cardio', e: 'Vélo', mw: 'stationary bike', t: true,
    requires: ['bike'], zones: ['quads'],
    substitutes: [{ when: 'no_bike', use: 'burpees' }],
  },
  burpees: {
    id: 'burpees', n: 'Burpees', m: 'Cardio', e: 'Aucun', mw: 'burpee',
    requires: [], zones: ['chest', 'quads', 'shoulders'],
  },
  mobilite: {
    id: 'mobilite', n: 'Routine mobilité', m: 'Mobilité', e: 'Aucun', mw: 'full body mobility', t: true,
    requires: [], zones: [],
    guide: [
      { name: 'Rotations cou & épaules',     duration: 60,  desc: 'Cercles lents dans les 2 sens, respiration calme' },
      { name: 'Cercles de bras',              duration: 60,  desc: 'Petits puis grands cercles, avant puis arrière' },
      { name: 'Cat-Cow (chat-vache)',         duration: 90,  desc: '4 pattes, dos rond / dos creux, synchro respiration' },
      { name: 'Bird-Dog',                     duration: 90,  desc: '4 pattes, bras + jambe opposés tendus, alterner' },
      { name: "World's Greatest Stretch",     duration: 120, desc: 'Fente basse + rotation thoracique, 4 par jambe' },
      { name: 'Hip Openers 90/90',            duration: 90,  desc: 'Assis jambes en 90/90, bascule lente d\'un côté à l\'autre' },
      { name: 'Inchworm + pompe',             duration: 90,  desc: 'Debout, mains au sol, marche en planche, 1 pompe, retour' },
      { name: 'Squats lents amplitude max',   duration: 60,  desc: 'Squat poids du corps, descente lente, talons au sol' },
      { name: 'T-Spine rotations',            duration: 90,  desc: 'À genoux, main derrière tête, ouverture thorax côté ciel' },
      { name: 'Respiration profonde',         duration: 60,  desc: 'Allongé, mains sur ventre, 4s inspire / 6s expire' },
    ],
  },
  etirements: {
    id: 'etirements', n: 'Étirements', m: 'Mobilité', e: 'Aucun', mw: 'full body stretching', t: true,
    requires: [], zones: [],
    guide: [
      { name: 'Ischios assis · jambe G',      duration: 45, desc: 'Jambe tendue, main vers la pointe du pied, sans à-coup' },
      { name: 'Ischios assis · jambe D',      duration: 45 },
      { name: 'Quadriceps debout · G',        duration: 45, desc: 'Talon vers la fesse, bassin neutre, genoux alignés' },
      { name: 'Quadriceps debout · D',        duration: 45 },
      { name: 'Mollets contre mur · G',       duration: 45, desc: 'Jambe arrière tendue, talon au sol, pression sur le mur' },
      { name: 'Mollets contre mur · D',       duration: 45 },
      { name: 'Fente basse psoas · G',        duration: 60, desc: 'Genou arrière au sol, bassin avancé, sentir l\'aine' },
      { name: 'Fente basse psoas · D',        duration: 60 },
      { name: 'Papillon (adducteurs)',        duration: 90, desc: 'Assis, plantes de pieds collées, coudes sur les genoux' },
      { name: 'Pigeon · G',                   duration: 60, desc: 'Jambe avant pliée 90°, jambe arrière tendue, buste avancé' },
      { name: 'Pigeon · D',                   duration: 60 },
      { name: 'Cobra (lombaires)',            duration: 60, desc: 'Allongé sur le ventre, mains sous épaules, buste en arrière' },
      { name: 'Posture de l\'enfant',         duration: 60, desc: 'À genoux, fessiers sur talons, bras tendus loin' },
      { name: 'Torsion vertébrale · G',       duration: 30, desc: 'Allongé sur le dos, jambe pliée croisée, regard opposé' },
      { name: 'Torsion vertébrale · D',       duration: 30 },
      { name: 'Cou + épaules',                duration: 60, desc: 'Inclinaisons latérales + rotations lentes' },
    ],
  },
};
